import 'amazon-connect-streams';
import { config } from '../config';

export type AgentStatus = 'init' | 'offline' | 'available' | 'busy' | 'acw' | 'error';
export type CallState = 'idle' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface CallInfo {
  contactId: string;
  state: CallState;
  direction: 'inbound' | 'outbound';
  phoneNumber: string;
  attributes: Record<string, string>;
  muted: boolean;
  onHold: boolean;
  startedAt?: number;
}

export interface SoftphoneSnapshot {
  agentStatus: AgentStatus;
  agentName: string;
  call: CallInfo | null;
}

type Listener = (s: SoftphoneSnapshot) => void;

class Softphone {
  private agent: connect.Agent | null = null;
  private contact: connect.Contact | null = null;
  private listeners = new Set<Listener>();
  private snapshot: SoftphoneSnapshot = { agentStatus: 'init', agentName: '', call: null };
  private initialized = false;

  init(container: HTMLElement) {
    if (this.initialized) return;
    this.initialized = true;
    connect.core.initCCP(container, {
      ccpUrl: `${config.connectInstanceUrl}/ccp-v2/`,
      loginPopup: true,
      loginPopupAutoClose: true,
      loginOptions: { autoClose: true, height: 600, width: 400 },
      region: config.connectRegion,
      softphone: { allowFramedSoftphone: true, disableRingtone: false },
      pageOptions: { enableAudioDeviceSettings: true, enablePhoneTypeSettings: false },
      ccpAckTimeout: 5000,
      ccpSynTimeout: 3000,
      ccpLoadTimeout: 10000,
    });

    connect.agent((agent) => {
      this.agent = agent;
      this.patch({ agentName: agent.getName(), agentStatus: this.mapAgentState(agent) });
      agent.onStateChange(() => this.patch({ agentStatus: this.mapAgentState(agent) }));
      agent.onSoftphoneError((e) => { console.error('softphone error', e); this.patch({ agentStatus: 'error' }); });
    });

    connect.contact((contact) => {
      if (contact.getType() !== connect.ContactType.VOICE) return;
      this.contact = contact;
      const sync = (state: CallState) => this.patch({ call: this.describe(contact, state) });
      sync(contact.isInbound() ? 'incoming' : 'connecting');
      contact.onConnecting(() => sync(contact.isInbound() ? 'incoming' : 'connecting'));
      contact.onAccepted(() => sync('connecting'));
      contact.onConnected(() => sync('connected'));
      contact.onEnded(() => sync('ended'));
      contact.onDestroy(() => { this.contact = null; this.patch({ call: null }); });
      contact.onRefresh(() => { if (this.snapshot.call) sync(this.snapshot.call.state); });
    });
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    fn(this.snapshot);
    return () => this.listeners.delete(fn);
  }

  setAvailable() { this.setState('Available'); }
  setOffline() { this.setState('Offline'); }

  accept() { this.contact?.accept(); }
  reject() { this.contact?.getAgentConnection()?.destroy(); }
  hangUp() { this.contact?.getAgentConnection()?.destroy(); }
  mute() { this.agent?.mute(); this.patchCall({ muted: true }); }
  unmute() { this.agent?.unmute(); this.patchCall({ muted: false }); }
  hold() { this.contact?.getInitialConnection()?.hold(); this.patchCall({ onHold: true }); }
  resume() { this.contact?.getInitialConnection()?.resume(); this.patchCall({ onHold: false }); }
  clearAcw() { this.contact?.clear({ success: () => undefined, failure: (e) => console.error('clear failed', e) }); }

  dial(e164: string) {
    if (!this.agent) throw new Error('agent not ready');
    this.agent.connect(connect.Endpoint.byPhoneNumber(e164), {
      success: () => undefined,
      failure: (err) => console.error('dial failed', err),
    });
  }

  private setState(name: string) {
    const st = this.agent?.getAgentStates().find((s) => s.name === name);
    if (st) this.agent?.setState(st, {});
  }

  private mapAgentState(agent: connect.Agent): AgentStatus {
    const st = agent.getState();
    if (st.type === connect.AgentStateType.ROUTABLE) return 'available';
    if (st.type === connect.AgentStateType.OFFLINE) return 'offline';
    if (st.type === connect.AgentStateType.ERROR) return 'error';
    if (st.name === 'AfterCallWork' || agent.getContacts().some((c) => c.getState().type === connect.ContactStateType.ENDED)) return 'acw';
    return 'busy';
  }

  private describe(contact: connect.Contact, state: CallState): CallInfo {
    const initial = contact.getInitialConnection();
    const endpoint = initial?.getEndpoint();
    const prev = this.snapshot.call;
    return {
      contactId: contact.getContactId(),
      state,
      direction: contact.isInbound() ? 'inbound' : 'outbound',
      phoneNumber: endpoint?.phoneNumber ?? '',
      attributes: Object.fromEntries(Object.entries(contact.getAttributes() ?? {}).map(([k, v]) => [k, (v as { value: string }).value])),
      muted: prev?.muted ?? false,
      onHold: prev?.onHold ?? false,
      startedAt: state === 'connected' ? prev?.startedAt ?? Date.now() : prev?.startedAt,
    };
  }

  private patchCall(p: Partial<CallInfo>) {
    if (this.snapshot.call) this.patch({ call: { ...this.snapshot.call, ...p } });
  }

  private patch(p: Partial<SoftphoneSnapshot>) {
    this.snapshot = { ...this.snapshot, ...p };
    for (const l of this.listeners) l(this.snapshot);
  }
}

export const softphone = new Softphone();
